import React, { useEffect, useMemo, useRef } from 'react';
import { Box, Card, Skeleton, Typography, Stack } from '@mui/material';
import { getCardsColorByName } from '../../../../../utils/constants/pointColors';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { getOverView } from '../../../../../redux/features/poinTrackerSlice';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';

export default function DashboardCards({ fromDate, toDate, selectedTeam }) {
  const dispatch = useDispatch();
  const { loading, responseData } = useSelector((state) => state.pointTracker.overView);
  const dispatchParams = useMemo(() => {
    return {
      fromDate,
      toDate,
      wingId: selectedTeam?.id === -1 ? null : selectedTeam?.id,
    };
  }, [fromDate, toDate, selectedTeam?.id]);

  useEffect(() => {
    if (fromDate && toDate) {
      dispatch(getOverView(dispatchParams));
    }
  }, [dispatch, dispatchParams]);

  const formatCurrency = (value) => `₹${Number(value || 0).toLocaleString('en-IN')}`;

  const cardsOverview = responseData?.data ?? {};
  const dashboardData = [
    {
      title: 'Total Revenue',
      value: formatCurrency(cardsOverview?.totalRevenue || 0),
      percent: cardsOverview?.trends?.totalRevenue,
    },
    {
      title: 'Total Sales',
      value: cardsOverview?.totalSales,
      percent: cardsOverview?.trends?.totalSales,
    },
    {
      title: 'TAR',
      value: `${cardsOverview?.tar || 0}%`,
      percent: cardsOverview?.trends?.tar,
    },
    {
      title: 'ACR',
      value: `${cardsOverview?.acr || 0}%`,
      percent: cardsOverview?.trends?.acr,
    },
    {
      title: 'SFR',
      value: `${cardsOverview?.sfr || 0}%`,
      percent: cardsOverview?.trends?.sfr,
    },
    {
      title: 'LCR',
      value: `${cardsOverview?.lcr || 0}%`,
      percent: cardsOverview?.trends?.lcr,
    },
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        flexWrap: 'nowrap',
        overflowX: 'auto',
        pb: 1,

        '&::-webkit-scrollbar': {
          display: 'none',
        },
        mt: 1,
      }}
    >
      {loading ? (
        [...Array(6)].map((_, index) => (
          <Card
            key={index}
            sx={{
              minWidth: 180,
              flexShrink: 0,
              flex: '1 1 180px',
              height: 120,
              p: 2,
              borderRadius: 3,
            }}
          >
            {/* Title */}
            <Skeleton width="60%" height={20} animation="wave" />

            {/* Value */}
            <Skeleton width="80%" height={30} animation="wave" />

            {/* Subtitle */}
            <Skeleton width="40%" height={15} animation="wave" />

            {/* Change */}
            <Skeleton width="30%" height={15} animation="wave" />
          </Card>
        ))
      ) : (
        <>
          {dashboardData.map((item, index) => {
            const { color, border } = getCardsColorByName(item.title);
            return (
              <Card
                key={index}
                sx={{
                  minWidth: 180, //  important for scroll
                  flexShrink: 0, //  prevents shrinking
                  flex: '1 1 180px', // equal width
                  height: 120, // same height
                  padding: 2,
                  borderRadius: 3,
                  backgroundColor: color,
                  boxShadow: 3,
                  border: `1px solid ${border}`,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {item.title}
                </Typography>

                <Typography variant="h5" fontWeight="bold">
                  {item.value}
                </Typography>

                {item.percent !== undefined && item.percent !== null && (
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    {item.percent > 0 ? (
                      <TrendingUpIcon sx={{ fontSize: 16, color: 'green' }} />
                    ) : (
                      <TrendingDownIcon sx={{ fontSize: 16, color: 'red' }} />
                    )}

                    <Typography variant="caption" color={item.percent > 0 ? 'green' : 'red'}>
                      {item.percent}%
                    </Typography>
                  </Stack>
                )}
              </Card>
            );
          })}
        </>
      )}
    </Box>
  );
}
